export const getApiBaseUrl = () =>
  (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');

export const buildApiUrl = (endpoint = '') => {
  const path = String(endpoint).replace(/^\//, '');
  const base = getApiBaseUrl();
  // Empty base = same-origin absolute path (/api/...), never "undefined/api/..."
  return base ? `${base}/${path}` : `/${path}`;
};

export const callApi = (_endpoint, _request) => {
  return fetch(buildApiUrl(_endpoint), _request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          if (!response.ok) {
            const error = { ...json, status: response.status };
            return Promise.reject(error);
          }
          return json;
        });
      }
      if (response.ok) {
        return response
          .text()
          .then((text) => {
            try {
              const json = JSON.parse(text);
              if (json) {
                return json;
              }
            } catch (e) {
              // ignore parse error
            }
            return { responseText: text };
          })
          .catch((e) => {
            return { error: e };
          });
      }
      const error = new Error(`${response.status} - ${response.statusText}`);
      error.status = response.status;
      throw error;
    })
    .catch((e) => {
      throw e;
    });
};

const REFRESH_SKEW_MS = 60 * 1000;
let refreshPromise = null;

const buildAuthHeaders = (token, extra = {}) => {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra,
  };
  if (token) {
    headers.Authorization = token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }
  return headers;
};

const refreshAccessToken = async () => {
  const { getStore } = await import('./storeRef');
  const store = getStore();
  if (!store) {
    throw new Error('Store not ready');
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const { refreshTokenSuccess, logoutUser, normalizeAuthPayload } =
      await import('./auth/actions');
    const state = store.getState();
    const { refreshToken } = state.authUser.currentUser || {};

    if (!refreshToken) {
      store.dispatch(logoutUser());
      throw new Error('Missing refresh token');
    }

    try {
      const result = await callApi('api/user/refresh', {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const normalized = normalizeAuthPayload(result);
      if (!normalized.token) {
        throw new Error('Invalid refresh response');
      }

      store.dispatch(
        refreshTokenSuccess({
          token: normalized.token,
          refreshToken: normalized.refreshToken,
          expiresAt: normalized.expiresAt,
          tokenType: normalized.tokenType,
        })
      );

      return normalized.token;
    } catch (error) {
      store.dispatch(logoutUser());
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const ensureValidAccessToken = async (token) => {
  const { getStore } = await import('./storeRef');
  const store = getStore();
  const currentUser = store?.getState()?.authUser?.currentUser || {};
  const accessToken = token || currentUser.token;
  const { expiresAt, refreshToken } = currentUser;

  if (!accessToken && !refreshToken) {
    return '';
  }

  const needsRefresh =
    !accessToken || (expiresAt && Date.now() >= expiresAt - REFRESH_SKEW_MS);

  if (needsRefresh && refreshToken) {
    return refreshAccessToken();
  }

  return accessToken;
};

const withAuthRequest = async ({ query, token, method, body }) => {
  let accessToken = await ensureValidAccessToken(token);

  const doRequest = (authToken) =>
    callApi(query, {
      headers: buildAuthHeaders(authToken),
      ...(method ? { method } : {}),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  try {
    return await doRequest(accessToken);
  } catch (error) {
    if (error?.status === 401) {
      accessToken = await refreshAccessToken();
      return doRequest(accessToken);
    }
    throw error;
  }
};

export const withloadAsync = ({ query, token }) =>
  withAuthRequest({ query, token });

export const withPostPutDeleteAsync = ({ query, body, method, token }) =>
  withAuthRequest({ query, token, method, body });

export { buildAuthHeaders, ensureValidAccessToken };
